import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { Lock, Eye, EyeOff, Check, Shield } from 'lucide-react';
import apiClient from '../../services/apiClient';
import { useAuth } from '../../contexts/AuthContext';

function ChangePasswordSection() {
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { logout } = useAuth();

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm();

  const newPassword = watch('newPassword');

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      const response = await apiClient.post('/api/users/change-password', {
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      });

      if (response.success) {
        toast.success('Password changed successfully! Please sign in again.');
        reset();
        // Logout user after successful password change
        setTimeout(async () => {
          await logout();
        }, 2000);
      }
    } catch (err) {
      // Handle different error types
      if (err.message?.includes('Google sign-in')) {
        toast.error(
          'Your account uses Google sign-in. To set a password, please sign out and use "Forgot Password" on the login page.',
          { duration: 6000 }
        );
      } else if (err.message?.includes('Current password is incorrect')) {
        toast.error('The current password you entered is incorrect. Please try again.');
      } else {
        toast.error(err.message || 'Failed to change password');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="rounded-[20px] border bg-white dark:bg-white/[0.02] border-gray-200 dark:border-white/[0.06] overflow-hidden">
      <div className="px-5 sm:px-6 md:px-8 py-4 sm:py-5 border-b border-gray-100 dark:border-white/[0.06]">
        <div className="flex items-start sm:items-center gap-2 sm:gap-3">
          <div className="w-9 h-9 rounded-[10px] flex items-center justify-center bg-gray-50 dark:bg-white/[0.04] flex-shrink-0">
            <Shield className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 dark:text-gray-500" />
          </div>
          <div className="min-w-0">
            <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-900 dark:text-white">Change Password</h3>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-light mt-0.5 sm:mt-1">Update your password to keep your account secure</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="p-4 sm:p-6 md:p-8">
        <div className="space-y-4 sm:space-y-6">
          {/* Current Password */}
          <div>
            <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Current Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400 dark:text-gray-600" />
              </div>
              <input
                {...register('currentPassword', {
                  required: 'Current password is required',
                })}
                type={showCurrentPassword ? 'text' : 'password'}
                className={`
                  block w-full pl-10 pr-10 py-3 border rounded-[12px]
                  ${errors.currentPassword
                    ? 'border-red-500/40 focus:border-red-500 focus:ring-red-500/30'
                    : 'border-gray-200 dark:border-white/[0.08] focus:ring-blue-500/30 focus:border-blue-500/50'
                  }
                  bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white
                  placeholder-gray-400 dark:placeholder-gray-600
                  focus:outline-none focus:ring-1
                  transition-colors
                `}
                placeholder="Enter current password"
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showCurrentPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {errors.currentPassword.message}
              </p>
            )}
          </div>

          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400 dark:text-gray-600" />
              </div>
              <input
                {...register('newPassword', {
                  required: 'New password is required',
                  minLength: {
                    value: 8,
                    message: 'Password must be at least 8 characters',
                  },
                  pattern: {
                    value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&.#^()\-_=+\[\]{}|;:,<>\/\\])/,
                    message: 'Password must contain uppercase, lowercase, number, and special character',
                  },
                })}
                type={showNewPassword ? 'text' : 'password'}
                className={`
                  block w-full pl-10 pr-10 py-3 border rounded-[12px]
                  ${errors.newPassword
                    ? 'border-red-500/40 focus:border-red-500 focus:ring-red-500/30'
                    : 'border-gray-200 dark:border-white/[0.08] focus:ring-blue-500/30 focus:border-blue-500/50'
                  }
                  bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white
                  placeholder-gray-400 dark:placeholder-gray-600
                  focus:outline-none focus:ring-1
                  transition-colors
                `}
                placeholder="Enter new password"
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showNewPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {errors.newPassword.message}
              </p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Confirm New Password
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400 dark:text-gray-600" />
              </div>
              <input
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (value) => value === newPassword || 'Passwords do not match',
                })}
                type={showConfirmPassword ? 'text' : 'password'}
                className={`
                  block w-full pl-10 pr-10 py-3 border rounded-[12px]
                  ${errors.confirmPassword
                    ? 'border-red-500/40 focus:border-red-500 focus:ring-red-500/30'
                    : 'border-gray-200 dark:border-white/[0.08] focus:ring-blue-500/30 focus:border-blue-500/50'
                  }
                  bg-white dark:bg-white/[0.03] text-gray-900 dark:text-white
                  placeholder-gray-400 dark:placeholder-gray-600
                  focus:outline-none focus:ring-1
                  transition-colors
                `}
                placeholder="Confirm new password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                {showConfirmPassword ? (
                  <EyeOff className="h-5 w-5" />
                ) : (
                  <Eye className="h-5 w-5" />
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                {errors.confirmPassword.message}
              </p>
            )}
          </div>

          {/* Password Requirements */}
          <div className="rounded-[12px] bg-gray-50 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Password Requirements:
            </p>
            <ul className="space-y-1 text-sm text-gray-500 dark:text-gray-500">
              <li className="flex items-center">
                <Check className={`w-4 h-4 mr-2 ${newPassword?.length >= 8 ? 'text-green-500' : 'text-gray-400'}`} />
                At least 8 characters
              </li>
              <li className="flex items-center">
                <Check className={`w-4 h-4 mr-2 ${/[A-Z]/.test(newPassword) ? 'text-green-500' : 'text-gray-400'}`} />
                One uppercase letter
              </li>
              <li className="flex items-center">
                <Check className={`w-4 h-4 mr-2 ${/[a-z]/.test(newPassword) ? 'text-green-500' : 'text-gray-400'}`} />
                One lowercase letter
              </li>
              <li className="flex items-center">
                <Check className={`w-4 h-4 mr-2 ${/\d/.test(newPassword) ? 'text-green-500' : 'text-gray-400'}`} />
                One number
              </li>
              <li className="flex items-center">
                <Check className={`w-4 h-4 mr-2 ${/[@$!%*?&.#^()\-_=+\[\]{}|;:,<>\/\\]/.test(newPassword) ? 'text-green-500' : 'text-gray-400'}`} />
                One special character
              </li>
            </ul>
          </div>

          {/* Warning */}
          <div className="rounded-[12px] bg-yellow-500/[0.05] border border-yellow-500/20 p-4">
            <p className="text-sm text-yellow-700 dark:text-yellow-300">
              <strong>Note:</strong> After changing your password, you'll be logged out and need to sign in again with your new password.
            </p>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full sm:w-auto px-5 sm:px-6 py-2.5 sm:py-3 bg-black dark:bg-white text-white dark:text-black hover:bg-gray-800 dark:hover:bg-gray-200 font-medium rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Changing Password...</span>
                </>
              ) : (
                <>
                  <Lock className="w-5 h-5" />
                  <span>Change Password</span>
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default ChangePasswordSection;
